import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingBag, Package, Clock, MapPin, Phone, Mail, LogOut, Eye, 
  Settings, Home, ShoppingCart, TrendingUp, Award, ChevronDown, 
  CheckCircle2, Ban, Loader, Star, MessageCircle, Truck, DollarSign, Calendar
} from 'lucide-react';
import { ordersAPI, authAPI } from '../services/api';

export default function BuyerDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  
  // Récupération de l'utilisateur actuel.
  const user = authAPI.getCurrentUser(); 
  const profileMenuRef = useRef(null);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/80x80?text=Pas+d%27image';
    if (imagePath.startsWith('http')) return imagePath;
    const baseUrl = 'http://localhost:8000';
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${baseUrl}${cleanPath}`;
  };

  const getUserAvatar = () => {
    if (user?.profile_photo) return getImageUrl(user.profile_photo);
    if (user?.profile_photo_url) return user.profile_photo_url;
    return null;
  };

  // FIX: Utilisation de useCallback pour garantir la stabilité de la fonction et éviter les boucles.
  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ordersAPI.getAll();
      setOrders(Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement commandes:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []); // Dépendances vides: la fonction ne change jamais.

  useEffect(() => {
    // Vérification de l'utilisateur (s'exécute à chaque rendu, mais ne cause pas de boucle)
    if (!user || user.user_type !== 'acheteur') {
      navigate('/login');
      return;
    }
    
    // FIX: Appel à loadOrders. Le useEffect ne se re-déclenchera pas de façon infinie 
    // car 'user' n'est plus une dépendance volatile.
    loadOrders();

    // Fermer le menu si l'utilisateur clique à l'extérieur
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [navigate, loadOrders]); // 'loadOrders' est une dépendance stable. 'user' a été retiré.

  const handleLogout = () => {
    authAPI.logout();
    navigate('/login');
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        icon: Clock,
        label: 'En attente',
        color: 'yellow',
        gradient: 'from-yellow-500 to-orange-500',
        bgClass: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
        description: 'Commande en cours de validation'
      },
      processing: {
        icon: Loader,
        label: 'En cours',
        color: 'blue',
        gradient: 'from-blue-500 to-cyan-500',
        bgClass: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
        description: 'Préparation de votre commande'
      },
      shipped: {
        icon: Truck,
        label: 'Expédié',
        color: 'purple',
        gradient: 'from-purple-500 to-pink-500',
        bgClass: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
        description: 'Commande en cours de livraison'
      },
      delivered: {
        icon: CheckCircle2,
        label: 'Livré',
        color: 'green',
        gradient: 'from-green-500 to-emerald-500',
        bgClass: 'bg-green-500/20 text-green-400 border-green-500/50',
        description: 'Commande bien reçue'
      },
      cancelled: {
        icon: Ban,
        label: 'Annulé',
        color: 'red',
        gradient: 'from-red-500 to-pink-500',
        bgClass: 'bg-red-500/20 text-red-400 border-red-500/50',
        description: 'Commande annulée'
      }
    };
    return configs[status] || configs.pending;
  };
  
  const userAvatarUrl = getUserAvatar();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-orange-500/30 border-t-orange-500 mx-auto mb-6"></div>
            <div className="absolute inset-0 rounded-full h-20 w-20 border-4 border-orange-500/10 blur-xl mx-auto animate-pulse"></div>
          </div>
          <div className="text-xl text-gray-300 font-medium animate-pulse">Chargement de votre espace...</div>
        </div>
      </div>
    );
  }

  const stats = {
    totalOrders: orders.length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    pending: orders.filter(o => o.status === 'pending' || o.status === 'processing' || o.status === 'shipped').length,
    totalSpent: orders.reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0)
  };

  const AvatarFallback = ({ username, size = '10' }) => (
    <div className={`relative w-${size} h-${size} bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg`}>
      {username?.charAt(0).toUpperCase()}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-20 w-96 h-96 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 -right-20 w-96 h-96 bg-gradient-to-tl from-purple-500/15 to-blue-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '500ms' }}></div>
      </div>
      
      {/* Header - Plus opaque */}
      <header className="bg-gray-900/90 backdrop-blur-lg shadow-2xl sticky top-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div 
                className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent cursor-pointer hover:scale-105 transition-transform"
                onClick={() => navigate('/')}
              >
                MonMarket
              </div>
              <span className="hidden sm:block text-white/20">|</span>
              <div className="hidden sm:flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-orange-500/20 to-pink-500/20 rounded-xl border border-orange-500/30">
                <ShoppingCart size={18} className="text-orange-400" />
                <span className="text-white/90 font-medium text-sm">Espace Client</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => navigate('/')} 
                className="group p-2.5 text-white/70 hover:text-orange-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
                title="Accueil"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <Home size={20} className="relative z-10" />
              </button>
              
              <button 
                onClick={() => navigate('/cart')} 
                className="group p-2.5 text-white/70 hover:text-orange-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
                title="Panier"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <ShoppingCart size={20} className="relative z-10" />
              </button>
              
              {/* Dropdown Profile Menu */}
              <div className="relative" ref={profileMenuRef}>
                <button 
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="flex items-center space-x-2 ml-4 pl-4 py-2 pr-4 border-l border-white/10 group bg-white/5 rounded-2xl hover:bg-white/10 transition-all"
                >
                  <div className="relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full blur opacity-75 group-hover:opacity-100 transition"></div>
                    {userAvatarUrl ? (
                        <img 
                            src={userAvatarUrl}
                            alt="Avatar"
                            className="relative w-10 h-10 rounded-full object-cover border-2 border-white/50 shadow-lg"
                            onError={(e) => e.target.outerHTML = `<div class="relative w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg">${user?.username?.charAt(0).toUpperCase()}</div>`}
                        />
                    ) : (
                        <AvatarFallback username={user?.username} />
                    )}
                  </div>
                  <div className="text-left hidden sm:block">
                    <div className="text-sm font-semibold text-white">{user?.username}</div>
                    <div className="text-xs text-purple-400/80">BIENVENU(E)</div>
                  </div>
                  <ChevronDown size={16} className={`text-white transition-transform ${isProfileMenuOpen ? 'rotate-180' : 'rotate-0'}`} />
                </button>
                
                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-3 w-72 sm:w-80 p-4 origin-top-right bg-gray-800 backdrop-blur-lg rounded-2xl shadow-3xl ring-1 ring-white/20 transition-all animate-in zoom-in-95 duration-300">
                    <div className="text-center mb-6 border-b border-gray-700 pb-4">
                        <div className="relative inline-block mb-4">
                            <div className="absolute -inset-2 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full blur animate-pulse"></div>
                            {userAvatarUrl ? (
                                <img 
                                    src={userAvatarUrl}
                                    alt="Avatar"
                                    className="relative w-24 h-24 rounded-full object-cover border-4 border-white shadow-2xl"
                                    onError={(e) => e.target.outerHTML = `<div class="relative w-24 h-24 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white text-3xl font-black shadow-2xl">${user?.username?.charAt(0).toUpperCase()}</div>`}
                                />
                            ) : (
                                <div className="relative w-24 h-24 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white text-3xl font-black shadow-2xl">
                                    {user?.username?.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                        <h3 className="text-xl font-black text-white mb-1">{user?.username}</h3>
                        <div className="inline-flex items-center space-x-2 bg-orange-500/20 px-3 py-1 rounded-full border border-orange-500/30">
                            <Award size={14} className="text-orange-400" />
                            <span className="text-xs text-orange-300 font-medium">Membre depuis {new Date(user?.date_joined || Date.now()).getFullYear()}</span>
                        </div>
                    </div>

                    <div className="space-y-3 mb-6 border-b border-gray-700 pb-4">
                        <div className="group/item flex items-center space-x-3 text-gray-300 bg-gray-700/50 rounded-xl p-3 hover:bg-gray-700 transition-all border border-gray-700 hover:border-orange-500/30">
                            <div className="w-8 h-8 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-lg flex items-center justify-center">
                                <Mail size={16} className="text-orange-400" />
                            </div>
                            <span className="text-sm truncate font-medium">{user?.email}</span>
                        </div>
                        
                        {user?.phone && (
                            <div className="group/item flex items-center space-x-3 text-gray-300 bg-gray-700/50 rounded-xl p-3 hover:bg-gray-700 transition-all border border-gray-700 hover:border-orange-500/30">
                                <div className="w-8 h-8 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-lg flex items-center justify-center">
                                    <Phone size={16} className="text-orange-400" />
                                </div>
                                <span className="text-sm font-medium">{user.phone}</span>
                            </div>
                        )}
                        
                        {user?.address && (
                            <div className="group/item flex items-start space-x-3 text-gray-300 bg-gray-700/50 rounded-xl p-3 hover:bg-gray-700 transition-all border border-gray-700 hover:border-orange-500/30">
                                <div className="w-8 h-8 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <MapPin size={16} className="text-orange-400" />
                                </div>
                                <span className="text-sm font-medium">{user.address}</span>
                            </div>
                        )}
                    </div>
                    
                    <button 
                      onClick={() => { navigate('/profile/edit'); setIsProfileMenuOpen(false); }}
                      className="group/btn relative w-full overflow-hidden mb-3"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover/btn:opacity-100 transition-opacity rounded-xl"></div>
                      <div className="relative bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-bold shadow-2xl transform group-hover/btn:scale-[1.02] transition-all flex items-center justify-center space-x-2 text-sm">
                        <Settings size={18} />
                        <span>Modifier le profil</span>
                      </div>
                    </button>
                    
                    <button 
                      onClick={handleLogout} 
                      className="group/btn relative w-full overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-red-600 opacity-0 group-hover/btn:opacity-100 transition-opacity rounded-xl"></div>
                      <div className="relative bg-red-500/20 text-red-400 py-3 rounded-xl font-bold shadow-2xl transform group-hover/btn:scale-[1.02] transition-all flex items-center justify-center space-x-2 text-sm border border-red-500/30">
                        <LogOut size={18} />
                        <span>Déconnexion</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
              
              {/* Logout button (visible only on small screens or as a secondary option) */}
              <button 
                onClick={handleLogout} 
                className="group p-2.5 text-red-400/80 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all ml-2 relative overflow-hidden hidden" // Cache par défaut
                title="Déconnexion"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-red-500/0 to-red-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <LogOut size={20} className="relative z-10" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 relative z-10">
        <h1 className="text-3xl sm:text-4xl font-black text-white mb-8 flex items-center space-x-3">
            <TrendingUp className="text-orange-400" size={36} />
            <span>Votre Tableau de Bord Client</span>
        </h1>
        
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Main Content spans all columns for full width display */}
          <div className="lg:col-span-4">
            
            {/* Stats Cards - Style harmonisé avec OrdersPage */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-10">
              {[
                {
                  icon: ShoppingBag,
                  label: 'Commandes',
                  value: stats.totalOrders,
                  gradient: 'from-blue-500 to-cyan-500',
                  action: () => navigate('/orders')
                },
                {
                  icon: CheckCircle2,
                  label: 'Livrées',
                  value: stats.delivered,
                  gradient: 'from-green-500 to-emerald-500',
                  action: () => navigate('/orders')
                },
                {
                  icon: Clock,
                  label: 'En cours',
                  value: stats.pending,
                  gradient: 'from-yellow-500 to-orange-500',
                  action: () => navigate('/orders')
                },
                {
                  icon: DollarSign, // Utilise l'icône DollarSign
                  label: 'Dépensé',
                  value: `${(stats.totalSpent / 1000).toFixed(0)}k`,
                  gradient: 'from-purple-500 to-pink-500',
                  subtitle: 'FCFA'
                }
              ].map((stat, idx) => (
                <div 
                  key={idx}
                  onMouseEnter={() => setHoveredCard(idx)}
                  onMouseLeave={() => setHoveredCard(null)}
                  onClick={stat.action}
                  className="group relative cursor-pointer"
                >
                  <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.gradient} rounded-2xl blur opacity-0 group-hover:opacity-30 transition duration-500`}></div>
                  <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl shadow-2xl p-5 border border-white/20 hover:bg-white/15 transition-all transform hover:scale-105 hover:-translate-y-1">
                    <div className={`w-12 h-12 bg-gradient-to-br ${stat.gradient} rounded-xl flex items-center justify-center mb-3 shadow-lg transform group-hover:rotate-12 transition-transform`}>
                      <stat.icon className="text-white" size={24} />
                    </div>
                    <div className="text-3xl font-black text-white mb-1 transform group-hover:scale-110 transition-transform origin-left">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-400 font-medium">{stat.label}</div>
                    {stat.subtitle && (
                      <div className="text-xs text-gray-500 mt-1">{stat.subtitle}</div>
                    )}
                    
                    {hoveredCard === idx && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-pulse"></div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Orders Section - NEON EFFECT REMOVED */}
            <div className="group/section relative">
              {/* Le div avec l'effet néon a été retiré ici pour la section */}
              <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl p-6 sm:p-8 border border-white/20 hover:bg-white/15 transition-all">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl sm:text-3xl font-black text-white flex items-center space-x-3">
                    <TrendingUp className="text-orange-400" size={32} />
                    <span>Commandes récentes</span>
                  </h2>
                  <button 
                    onClick={() => navigate('/orders')}
                    className="group/btn relative overflow-hidden flex items-center space-x-2 bg-gradient-to-r from-orange-500/20 to-pink-500/20 px-4 sm:px-6 py-2.5 rounded-xl hover:from-orange-500/30 hover:to-pink-500/30 transition-all border border-orange-500/30 hover:border-orange-500/50"
                  >
                    <span className="text-orange-300 font-medium text-sm">Voir tout</span>
                    <Eye size={16} className="text-orange-400 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </div>
                
                {orders.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="relative inline-block mb-6">
                      <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-2xl animate-pulse"></div>
                      <div className="relative w-24 h-24 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-xl border border-white/20">
                        <ShoppingBag size={48} className="text-orange-400" />
                      </div>
                    </div>
                    <p className="text-2xl font-bold mb-3 text-white">Aucune commande</p>
                    <p className="text-gray-400 mb-8 max-w-md mx-auto">Découvrez nos produits et commencez vos achats dès maintenant !</p>
                    <button 
                      onClick={() => navigate('/')} 
                      className="group/shop relative overflow-hidden inline-flex items-center space-x-2"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-pink-600 opacity-0 group-hover/shop:opacity-100 transition-opacity rounded-2xl"></div>
                      <div className="relative bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-2xl font-bold shadow-2xl transform group-hover/shop:scale-105 transition-all flex items-center space-x-2">
                        <ShoppingCart size={20} />
                        <span>Parcourir les produits</span>
                      </div>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {orders.slice(0, 3).map((order, idx) => {
                      const statusConfig = getStatusConfig(order.status);
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <div 
                          key={order.id}
                          className="group relative"
                          onClick={() => navigate('/orders')}
                          style={{ animationDelay: `${idx * 100}ms` }}
                        >
                          {/* ✅ CORRECTION : Opacité réduite à 5% pour un effet de halo très subtil */}
                          <div className={`absolute -inset-0.5 bg-gradient-to-r ${statusConfig.gradient} rounded-3xl blur opacity-0 group-hover:opacity-5 transition duration-500`}></div>
                          <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl overflow-hidden border border-white/20 hover:bg-white/15 transition-all transform group-hover:scale-[1.01] cursor-pointer">
                            {/* Header commande avec status */}
                            <div className={`relative overflow-hidden bg-gradient-to-r ${statusConfig.gradient} bg-opacity-20 p-6 border-b border-white/10`}>
                              <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent"></div>
                              <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                <div className="flex items-start space-x-4">
                                  <div className="relative">
                                    <div className={`absolute inset-0 bg-gradient-to-br ${statusConfig.gradient} rounded-2xl blur opacity-75 animate-pulse`}></div>
                                    <div className={`relative w-14 h-14 bg-gradient-to-br ${statusConfig.gradient} rounded-2xl flex items-center justify-center shadow-xl`}>
                                      <StatusIcon className="text-white" size={28} />
                                    </div>
                                  </div>
                                  <div>
                                    <div className="flex items-center space-x-3 mb-2">
                                      <h3 className="text-2xl font-black text-white">
                                        Commande #{order.id}
                                      </h3>
                                      <span className={`px-4 py-2 rounded-xl text-sm font-bold border-2 backdrop-blur-xl shadow-lg ${statusConfig.bgClass} flex items-center space-x-2`}>
                                        <span className="w-2 h-2 rounded-full bg-current animate-pulse"></span>
                                        <span>{statusConfig.label}</span>
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-300 mb-2 hidden sm:block">{statusConfig.description}</p>
                                    <div className="flex items-center space-x-4 text-sm text-gray-400">
                                      <div className="flex items-center space-x-1">
                                        <Calendar size={16} /> {/* Utilise l'icône Calendar */}
                                        <span>{new Date(order.created_at).toLocaleDateString('fr-FR', { 
                                          day: 'numeric', 
                                          month: 'long', 
                                          year: 'numeric' 
                                        })}</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <Package size={16} />
                                        <span>{order.items?.length || 0} article(s)</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm text-gray-400 mb-1 font-medium">Montant Total</div>
                                  <div className="text-4xl font-black text-white group-hover:text-orange-400 transition-colors">
                                    {parseFloat(order.total_amount).toLocaleString()}
                                  </div>
                                  <div className="text-sm text-gray-500">FCFA</div>
                                </div>
                              </div>
                            </div>

                            {/* Articles */}
                            <div className="p-6">
                              <h4 className="text-sm font-black text-gray-400 mb-4 uppercase tracking-wider flex items-center space-x-2">
                                <Package size={16} className="text-orange-400" />
                                <span>Articles commandés (les 2 premiers)</span>
                              </h4>
                              <div className="space-y-3">
                                {order.items?.slice(0, 2).map((item, itemIdx) => (
                                  <div 
                                    key={item.id || itemIdx}
                                    className="group/item relative overflow-hidden"
                                  >
                                    <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 via-orange-500/5 to-orange-500/0 opacity-0 group-hover/item:opacity-100 transition-opacity rounded-xl"></div>
                                    <div className="relative flex items-center space-x-4 bg-white/5 rounded-xl p-4 border border-white/10 group-hover/item:border-orange-500/30 transition-all">
                                      <div className="relative">
                                        <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl blur opacity-0 group-hover/item:opacity-50 transition"></div>
                                        <img 
                                          src={getImageUrl(item.product_image)}
                                          alt={item.product_name}
                                          className="relative w-20 h-20 rounded-2xl object-cover border-2 border-white/20 shadow-lg transform group-hover/item:scale-110 transition-transform"
                                          onError={(e) => {
                                            e.target.src = 'https://via.placeholder.com/80x80?text=Produit';
                                          }}
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="font-bold text-white truncate text-lg group-hover/item:text-orange-400 transition-colors">
                                          {item.product_name}
                                        </div>
                                        <div className="text-sm text-gray-400 flex items-center space-x-2 mt-1">
                                          <span className="font-medium">Quantité: {item.quantity}</span>
                                          <span className="text-gray-600">×</span>
                                          <span>{parseFloat(item.price).toLocaleString()} FCFA</span>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-2xl font-black text-white group-hover/item:text-orange-400 transition-colors">
                                          {parseFloat(item.subtotal).toLocaleString()}
                                        </div>
                                        <div className="text-xs text-gray-500">FCFA</div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {order.items?.length > 2 && (
                                    <div className="text-center py-2 bg-white/5 rounded-xl border border-white/10">
                                      <span className="text-sm text-orange-400 font-medium">
                                        +{order.items.length - 2} autre(s) produit(s)
                                      </span>
                                    </div>
                                  )}
                              </div>
                            </div>
                            
                            {/* Actions rapides */}
                            {order.status === 'delivered' && (
                                <div className="p-6 pt-0 flex flex-wrap gap-3">
                                    <button className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-400 rounded-xl hover:from-yellow-500/30 hover:to-orange-500/30 transition-all border border-yellow-500/30 hover:border-yellow-500/50 font-medium text-sm">
                                        <Star size={18} />
                                        <span>Laisser un avis</span>
                                    </button>
                                    <button className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-400 rounded-xl hover:from-blue-500/30 hover:to-cyan-500/30 transition-all border border-blue-500/30 hover:border-blue-500/50 font-medium text-sm">
                                        <MessageCircle size={18} />
                                        <span>Contacter le vendeur</span>
                                    </button>
                                </div>
                            )}
                            
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}